import React from 'react';
import {
  Alert,
  AlertActionLink,
  Bullseye,
  Button,
  Checkbox,
  Content,
  EmptyState,
  EmptyStateBody,
  Flex,
  FlexItem,
  HelperText,
  HelperTextItem,
  Label,
  LabelGroup,
  Spinner,
  TextInput,
  Title,
} from '@patternfly/react-core';
import { Table, Tbody, Td, Th, Thead, Tr } from '@patternfly/react-table';
import {
  CheckCircleIcon,
  OutlinedQuestionCircleIcon,
  SearchIcon,
  TimesCircleIcon,
} from '@patternfly/react-icons';
import { formatPrice } from '../../../../utils/money';
import type { DomainAvailabilityResult, DomainOperationPrice } from '../../../../types/models';

interface SearchStepProps {
  // TLD seed (baseline prices)
  registrationPrices: DomainOperationPrice[];
  pricesLoading: boolean;
  pricesError: string | null;
  onRetryPrices: () => void;
  // Controlled search inputs (lifted to the page so they survive step navigation)
  query: string;
  onQueryChange: (value: string) => void;
  selectedTlds: string[];
  onSelectedTldsChange: (tlds: string[]) => void;
  // Availability results
  results: DomainAvailabilityResult[];
  searching: boolean;
  searchError: string | null;
  hasSearched: boolean;
  onSearch: (domain: string, tlds: string[]) => void;
  // Cart
  cart: DomainAvailabilityResult[];
  onAddToCart: (result: DomainAvailabilityResult) => void;
  onRemoveFromCart: (domain: string) => void;
}

function availabilityLabel(result: DomainAvailabilityResult): React.ReactNode {
  if (result.available === true) {
    return (
      <Label color="green" icon={<CheckCircleIcon />}>
        Available
      </Label>
    );
  }
  if (result.available === false) {
    return (
      <Label color="red" icon={<TimesCircleIcon />}>
        Taken
      </Label>
    );
  }
  return (
    <Label color="grey" icon={<OutlinedQuestionCircleIcon />}>
      Couldn&apos;t check
    </Label>
  );
}

const SearchStep: React.FunctionComponent<SearchStepProps> = ({
  registrationPrices,
  pricesLoading,
  pricesError,
  onRetryPrices,
  query,
  onQueryChange,
  selectedTlds,
  onSelectedTldsChange,
  results,
  searching,
  searchError,
  hasSearched,
  onSearch,
  cart,
  onAddToCart,
  onRemoveFromCart,
}) => {
  const trimmedQuery = query.trim();
  // Typing a full domain (with a dot) pins one TLD — the backend ignores `tlds` for an FQDN, so we
  // disable the checkbox list and send no TLDs.
  const looksLikeFqdn = trimmedQuery.includes('.');
  const canSearch = !searching && trimmedQuery !== '' && (looksLikeFqdn || selectedTlds.length > 0);

  // When a full domain is typed, surface which offered TLD its suffix matches (longest wins, so
  // `co.uk` beats `uk`) and show that checkbox as pinned/checked — so `.be` ticks itself for
  // `ancalagon.be` instead of sitting unchecked-and-greyed, which reads as "not selected".
  const lowerQuery = trimmedQuery.toLowerCase();
  const fqdnTld = looksLikeFqdn
    ? registrationPrices
        .map((p) => p.tld)
        .filter((tld) => lowerQuery.endsWith(`.${tld.toLowerCase()}`))
        .sort((a, b) => b.length - a.length)[0] ?? null
    : null;

  const cartDomains = new Set(cart.map((c) => c.domain));

  const toggleTld = (tld: string, checked: boolean) => {
    if (checked) {
      onSelectedTldsChange([...selectedTlds, tld]);
    } else {
      onSelectedTldsChange(selectedTlds.filter((t) => t !== tld));
    }
  };

  const triggerSearch = () => {
    if (!canSearch) {
      return;
    }
    onSearch(trimmedQuery, looksLikeFqdn ? [] : selectedTlds);
  };

  const renderTldChooser = () => {
    if (pricesLoading) {
      return (
        <Bullseye>
          <Spinner size="lg" aria-label="Loading supported TLDs" />
        </Bullseye>
      );
    }
    if (pricesError) {
      return (
        <Alert
          variant="warning"
          isInline
          title="Could not load the supported TLDs"
          actionLinks={<AlertActionLink onClick={onRetryPrices}>Retry</AlertActionLink>}
        >
          {pricesError} You can still search a full domain (e.g. <code>ancalagon.be</code>) above.
        </Alert>
      );
    }
    if (registrationPrices.length === 0) {
      return (
        <HelperText>
          <HelperTextItem>
            No registration TLDs are currently offered. Search a full domain to check it directly.
          </HelperTextItem>
        </HelperText>
      );
    }
    return (
      <Flex flexWrap={{ default: 'wrap' }} spaceItems={{ default: 'spaceItemsMd' }}>
        {registrationPrices.map((p) => (
          <FlexItem key={p.tld}>
            <Checkbox
              id={`reg-tld-${p.tld}`}
              label={`.${p.tld} — ${formatPrice(p.unit_price)}`}
              isChecked={looksLikeFqdn ? p.tld === fqdnTld : selectedTlds.includes(p.tld)}
              onChange={(_event, checked) => toggleTld(p.tld, checked)}
              isDisabled={looksLikeFqdn}
            />
          </FlexItem>
        ))}
      </Flex>
    );
  };

  const renderResults = () => {
    if (searching) {
      return (
        <Bullseye>
          <Spinner aria-label="Checking availability" />
        </Bullseye>
      );
    }
    if (searchError) {
      return (
        <Alert variant="danger" isInline title="The availability check failed">
          {searchError}
        </Alert>
      );
    }
    if (!hasSearched) {
      return null;
    }
    if (results.length === 0) {
      return (
        <EmptyState titleText="No domains to show" icon={SearchIcon} variant="lg" headingLevel="h3">
          <EmptyStateBody>No candidates were returned. Try a different label or TLD selection.</EmptyStateBody>
        </EmptyState>
      );
    }
    return (
      <Table aria-label="Domain availability results" variant="compact">
        <Thead>
          <Tr>
            <Th>Domain</Th>
            <Th>Availability</Th>
            <Th>Price (excl. VAT)</Th>
            <Th screenReaderText="Add to cart" />
          </Tr>
        </Thead>
        <Tbody>
          {results.map((r) => {
            const inCart = cartDomains.has(r.domain);
            const canAdd = r.available === true && r.supported;
            return (
              <Tr key={r.domain}>
                <Td dataLabel="Domain">{r.domain}</Td>
                <Td dataLabel="Availability">{availabilityLabel(r)}</Td>
                <Td dataLabel="Price (excl. VAT)">
                  {r.supported && r.price !== null ? (
                    <>
                      {formatPrice(r.price)}
                      {r.is_custom && (
                        <Label color="purple" isCompact className="pf-v6-u-ml-sm">
                          custom
                        </Label>
                      )}
                    </>
                  ) : (
                    <Content component="small">Not offered</Content>
                  )}
                </Td>
                <Td dataLabel="Add to cart" modifier="fitContent">
                  {inCart ? (
                    <Label color="blue">In cart</Label>
                  ) : canAdd ? (
                    <Button variant="secondary" onClick={() => onAddToCart(r)}>
                      Add to cart
                    </Button>
                  ) : null}
                </Td>
              </Tr>
            );
          })}
        </Tbody>
      </Table>
    );
  };

  return (
    <Flex direction={{ default: 'column' }} spaceItems={{ default: 'spaceItemsLg' }}>
      <FlexItem>
        <Title headingLevel="h2" size="lg">
          Find your domain
        </Title>
        <Content component="p">
          Type a label (e.g. <code>ancalagon</code>) and tick the TLDs to check, or enter a full domain (e.g.{' '}
          <code>ancalagon.be</code>) to check one directly.
        </Content>
      </FlexItem>

      <FlexItem>
        <Flex
          spaceItems={{ default: 'spaceItemsSm' }}
          alignItems={{ default: 'alignItemsFlexStart' }}
        >
          <FlexItem grow={{ default: 'grow' }}>
            <TextInput
              type="text"
              aria-label="Domain label or full domain name"
              placeholder="ancalagon or ancalagon.be"
              value={query}
              onChange={(_event, value) => onQueryChange(value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  triggerSearch();
                }
              }}
            />
          </FlexItem>
          <FlexItem>
            <Button variant="primary" icon={<SearchIcon />} onClick={triggerSearch} isDisabled={!canSearch}>
              Search
            </Button>
          </FlexItem>
        </Flex>
      </FlexItem>

      <FlexItem>{renderTldChooser()}</FlexItem>

      <FlexItem>{renderResults()}</FlexItem>

      <FlexItem>
        <Title headingLevel="h3" size="md">
          Your cart ({cart.length})
        </Title>
        {cart.length === 0 ? (
          <HelperText>
            <HelperTextItem>Add at least one available domain to continue.</HelperTextItem>
          </HelperText>
        ) : (
          <LabelGroup numLabels={25} aria-label="Domains in your cart" className="pf-v6-u-mt-sm">
            {cart.map((c) => (
              <Label
                key={c.domain}
                color="blue"
                onClose={() => onRemoveFromCart(c.domain)}
                closeBtnAriaLabel={`Remove ${c.domain} from cart`}
              >
                {c.domain}
                {c.price !== null ? ` · ${formatPrice(c.price)}` : ''}
              </Label>
            ))}
          </LabelGroup>
        )}
      </FlexItem>
    </Flex>
  );
};

export default SearchStep;
